import { defineComponent as _defineComponent } from 'vue'
import { ref, reactive, onMounted, watch, computed } from "vue";
  import router from "@/router";
  import getUserRole from "@/helpers/getUserRole";
  import LandingMobileHeader from "@/components/landing/LandingMobileHeader.vue";
  import SwitchComponent from "@/components/ui/SwitchComponent.vue";
  import { useIsDesktop } from "@/features/useIsDesktop";
  import { RouteLandingNames } from "@/enums/RouteLandingNames";
  import {
    landingSubscriptionPlansData,
    landingDescriptionPlansData,
    landingProjectSalesAndMarketingData,
  } from "@/constants/landingData";
  import CardSubscription from "@/components/landing/CardSubscription.vue";
  import ProjectSalesAndMarketing from "@/components/landing/ProjectSalesAndMarketing.vue";
  import LandingFooter from "@/components/landing/LandingFooter.vue";
  import { headerTabs } from "@/constants/navigationTabs";
  import { SwitchItemHeader } from "@/types/SwitchItemHeader";
  import EstateMap from "@/components/EstateMap.vue";
  import StageMap from "@/components/StageMap.vue";
  import { UserRoles } from "@/enums/UserRoles";
  import { MapPages } from "@/enums/MapPages";
  import { isLogged } from "@/features/useUser";
  import ContactUsModal from "@/components/ui/modals/ContactUsModal.vue";
  import { EmbeddedMapQueries } from "@/types/EmbeddedMapQueries";
  import { LotMapInterface } from "@/types/LotMapInterface";
  import ReserveLotModal from "@/components/ui/modals/ReserveLotModal.vue";
  import store from "@/store";

  const MIN_DESKTOP_WIDTH = 992;
  
export default /*@__PURE__*/_defineComponent({
  __name: 'LandingPlans',
  setup(__props, { expose: __expose }) {
  __expose();

  const activeHeaderTab = ref<SwitchItemHeader | null>(null);
  const { isSalesPersonRole } = getUserRole();
  const { isDesktop } = useIsDesktop(MIN_DESKTOP_WIDTH);
  const mapEstateId = computed(() => process.env.VUE_APP_LANDING_MAP_ESTATE_ID);
  const zoom = ref(1);
  const mouseX = ref(0);
  const mouseY = ref(0);
  const showReserveModal = ref(false);
  const urlRedirectAfterVerifyEmail = ref("");
  const { isEmbedded, defaultView, showLogo } = embeddedOptions();
  const showStageMap = computed(() => {
    return router.currentRoute.value.query.stageId;
  });
  const estate = ref({});
  const transitionName = ref("fade");
  const lotForReserve = ref<LotMapInterface | null>(null);
  const lotBuilderIconHover = ref(false);

  function onMessage() {
    showReserveModal.value = false;
  }

  const visibleHeaderTabs = computed(() =>
    headerTabs.filter((tab) => !tab.hidden)
  );

  watch(
    router.currentRoute,
    (value) => {
      if (value.name === RouteLandingNames.LANDING_BUILDERS) {
        activeHeaderTab.value = headerTabs[1];
      } else if (value.name === RouteLandingNames.LANDING) {
        activeHeaderTab.value = headerTabs[0];
      } else if (value.name === RouteLandingNames.LANDING_PLANS) {
        activeHeaderTab.value = headerTabs[2];
      } else {
        activeHeaderTab.value = null;
      }
    },
    { immediate: true }
  );

  onMounted(() => {
    const routeNameMap = {
      [RouteLandingNames.LANDING_PLANS]: headerTabs[2],
      [RouteLandingNames.LANDING_BUILDERS]: headerTabs[1],
      [RouteLandingNames.LANDING]: headerTabs[0],
    };

    activeHeaderTab.value =
      routeNameMap[
        router.currentRoute.value.name as keyof typeof routeNameMap
      ] || null;
  });

  const onLogin = () => {
    router.push({ name: "SignIn" });
  };

  const onChangeHeaderTab = (value: SwitchItemHeader) => {
    router.push(value.path);
  };

  const updateZoom = (value: number) => {
    const newZoom = zoom.value + value;

    if (newZoom >= 1 && newZoom <= 2.6) {
      zoom.value = newZoom;
    }
  };

  function embeddedOptions() {
    const querys = router.currentRoute.value.query as EmbeddedMapQueries;
    const view: "list" | "map" = querys.default === "list" ? "list" : "map";

    return {
      isEmbedded: querys.embed === "true",
      defaultView: view,
      showLogo: querys.powered_by_logo !== "false",
    };
  }

  const scrollToSavedPosition = (position: number) => {
    setTimeout(() => {
      window.scrollTo(0, position);
    }, 20);
  };

  const onReserve = (lot: LotMapInterface) => {
    scrollTopEmbedded();
    transitionName.value = "fade";
    lotForReserve.value = lot;
    showReserveModal.value = true;
    const scrollPosition = window.scrollY;

    router.push({
      path: router.currentRoute.value.path,
      query: { ...router.currentRoute.value.query, lotId: lot.lotId._id },
    });
    scrollToSavedPosition(scrollPosition);
  };

  const scrollTopEmbedded = () => {
    if (!isEmbedded) {
      return;
    }

    parent.postMessage({ event: "scroll" }, "*");
  };

  function fetchEstate() {
    if (mapEstateId.value) {
      store.dispatch("estate/getEstate", mapEstateId.value).then((response) => {
        estate.value = response.data;
      });
    }
  }

  const closeReserveModal = (needUpdateRoute: boolean) => {
    showReserveModal.value = false;
    const scrollPosition = window.scrollY;

    if (needUpdateRoute) {
      router.push({
        path: router.currentRoute.value.path,
        query: { ...router.currentRoute.value.query, lotId: null },
      });
      scrollToSavedPosition(scrollPosition);
    }
  };

  onMounted(() => {
    fetchEstate();
  });

const __returned__ = { activeHeaderTab, isSalesPersonRole, MIN_DESKTOP_WIDTH, isDesktop, mapEstateId, zoom, mouseX, mouseY, showReserveModal, urlRedirectAfterVerifyEmail, isEmbedded, defaultView, showLogo, showStageMap, estate, transitionName, lotForReserve, lotBuilderIconHover, onMessage, visibleHeaderTabs, onLogin, onChangeHeaderTab, updateZoom, embeddedOptions, scrollToSavedPosition, onReserve, scrollTopEmbedded, fetchEstate, closeReserveModal, ref, reactive, onMounted, watch, computed, get router() { return router }, get getUserRole() { return getUserRole }, LandingMobileHeader, SwitchComponent, get useIsDesktop() { return useIsDesktop }, get RouteLandingNames() { return RouteLandingNames }, get landingSubscriptionPlansData() { return landingSubscriptionPlansData }, get landingDescriptionPlansData() { return landingDescriptionPlansData }, get landingProjectSalesAndMarketingData() { return landingProjectSalesAndMarketingData }, CardSubscription, ProjectSalesAndMarketing, LandingFooter, get headerTabs() { return headerTabs }, get SwitchItemHeader() { return SwitchItemHeader }, EstateMap, StageMap, get UserRoles() { return UserRoles }, get MapPages() { return MapPages }, get isLogged() { return isLogged }, ContactUsModal, get EmbeddedMapQueries() { return EmbeddedMapQueries }, get LotMapInterface() { return LotMapInterface }, ReserveLotModal, get store() { return store } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})